import { Drawer, IconButtonGroup, IconButtonGroupProps } from '@/components'
import { ResetReportSettingsButton } from '@/components/molecules/ResetReportSettingsButton/ResetReportSettingsButton'
import { IntervalReportSettings } from '@/hooks'
import {
    DataTypeParam,
    IntervalDisplayParam,
    IntervalTimeParam,
} from '@/hooks/useApi/useActiveManagementApi/types'
import {
    ActiveManagementFiltersValues,
    DEFAULT_INTERVAL_REPORT_SETTINGS,
} from '@/utils/constants'
import { useTranslation } from 'react-i18next'
import { Dispatch, SetStateAction } from 'react'
import { DisplaySettingsSelect } from './DisplaySettingsSelect'
import { TimezoneSelect } from './TimezoneSelect'
import { DataTypeSelect } from './DataTypeSelect'
import { TimeIntervalSelect } from './TimeIntervalSelect'

type IntervalDrawerProps = {
    activeFilters: ActiveManagementFiltersValues
    reportSettings: IntervalReportSettings
    setReportSettings: (settings: Partial<IntervalReportSettings>) => void
    isOpen: boolean
    setIsOpen: Dispatch<SetStateAction<boolean>>
    toggleButtonRef?: React.Ref<HTMLButtonElement>
    showResetReportSettingsButton?: boolean
}

export const IntervalDrawer: React.FC<IntervalDrawerProps> = (props) => {
    const {
        reportSettings,
        setReportSettings,
        activeFilters,
        isOpen,
        setIsOpen,
        toggleButtonRef,
        showResetReportSettingsButton,
    } = props
    const { t } = useTranslation('translations')
    const hasDefaultSettings =
        JSON.stringify(reportSettings) ===
        JSON.stringify(DEFAULT_INTERVAL_REPORT_SETTINGS)
    const onChangeReportSettings = (
        settings: Partial<IntervalReportSettings>
    ) => {
        setReportSettings({
            ...reportSettings,
            ...settings,
        })
    }

    const handleDisplayChange = (display: IntervalDisplayParam) => {
        onChangeReportSettings({ display })
    }

    const handleDataGroupingChange = (id: string) => {
        onChangeReportSettings({
            dataGrouping: id as IntervalReportSettings['dataGrouping'],
        })
    }

    const handleDataTypeChange = (dataType: DataTypeParam) => {
        onChangeReportSettings({ dataType })
    }

    const handleTimeIntervalChange = (timeInterval: IntervalTimeParam) => {
        onChangeReportSettings({ timeInterval })
    }

    const buttons: IconButtonGroupProps['buttons'] = [
        {
            id: 'aggregate',
            name: 'show_chart',
            label: t(
                'active_management.interval.report_settings.data_grouping.aggregate'
            ),
            size: 'medium',
            iconSize: 'small',
        },
        {
            id: 'compare',
            name: 'stacked_line_chart',
            label: t(
                'active_management.interval.report_settings.data_grouping.compare'
            ),
            size: 'medium',
            iconSize: 'small',
        },
    ]

    const isResetHidden =
        showResetReportSettingsButton !== undefined
            ? !showResetReportSettingsButton
            : hasDefaultSettings

    return (
        <Drawer
            drawerToggleLabel={t(
                'active_management.interval.report_settings.title'
            )}
            drawerToggleIconName="tune"
            title={t('active_management.interval.report_settings.title')}
            headerContent={
                <ResetReportSettingsButton
                    onClick={() => {
                        setReportSettings(DEFAULT_INTERVAL_REPORT_SETTINGS)
                    }}
                    isHidden={isResetHidden}
                    data-testid={'reset-settings-button'}
                />
            }
            isOpen={isOpen}
            setIsOpen={setIsOpen}
            toggleButtonRef={toggleButtonRef}
        >
            <div className="space-y-7">
                <div className="space-y-3">
                    <div className="text-heading-caps">
                        {t(
                            'active_management.interval.report_settings.data_grouping.label'
                        )}
                    </div>
                    <IconButtonGroup
                        className="w-full"
                        buttons={buttons}
                        variant="group"
                        onChange={handleDataGroupingChange}
                        activeId={reportSettings.dataGrouping}
                    />
                </div>
                <div className="space-y-3">
                    <div className="text-heading-caps">
                        {t(
                            'active_management.interval.report_settings.data_setup.label'
                        )}
                    </div>
                    <DisplaySettingsSelect
                        value={reportSettings.display}
                        onChange={handleDisplayChange}
                        activeFilters={activeFilters}
                        reportSettings={reportSettings}
                    />
                    <DataTypeSelect
                        value={reportSettings.dataType}
                        onChange={handleDataTypeChange}
                    />
                    <TimeIntervalSelect
                        value={reportSettings.timeInterval}
                        onChange={handleTimeIntervalChange}
                    />
                </div>
                <div className="space-y-3">
                    <div className="text-heading-caps">
                        {t(
                            'active_management.interval.report_settings.time_setup'
                        )}
                    </div>
                    <TimezoneSelect
                        value={(reportSettings.timezone as string) || ''}
                        onChange={(timezone) => {
                            onChangeReportSettings({ timezone })
                        }}
                    />
                </div>
            </div>
        </Drawer>
    )
}
